import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { useElectronAuth } from '#~/auth/protect.js';
import { ElectronAPI } from '#~/electron/index.js';
import { useLinkedRoom } from '#~/room/hook_last_id.js';
import { RoomWrapper } from '#~/routes/room/room.js';
import { Loader } from '@studio/design-system/loader';
import { motion } from '@studio/design-system/motion';
import { getCurrentVendorLogo } from '@studio/design-system/themes/vendor.js';
import { useEffect, useRef } from 'react';
import closeSvg from './icons/close.svg';
import openSvg from './icons/open.svg';
import { useTrayStore } from './store.js';
import { Linked, LoggedOut, Unlinked } from './states.js';

const ElectronTrayContent = () => {
  return (
    <>
      <div className='flex items-center justify-between'>
        <button
          className='px-16 py-12 text-[#9DA8BE] hover:opacity-75'
          onClick={() => {
            ElectronAPI.trayClose();
          }}
        >
          <img src={closeSvg} alt='Close tray' />
        </button>
        <div className='h-18 text-base font-demibold'>
          <img src={getCurrentVendorLogo()} className='h-18' alt='Vendor logo' />
        </div>
        <button
          className='px-16 py-12 text-secondary hover:opacity-75'
          onClick={() => {
            ElectronAPI.openStudio();
          }}
        >
          <img src={openSvg} className='text-[#9DA8BE]' alt='Open studio' />
        </button>
      </div>
      <div className='border-b-1 border-base-50' />
      <Linked />
    </>
  );
};

export const ElectronTray = () => {
  const { roomId, loading: roomIdLoading } = useLinkedRoom();
  const loadedRoomId = useRef(roomId);
  loadedRoomId.current = roomId ? roomId : loadedRoomId.current;

  const { show } = useTrayStore();
  const ref = useRef<HTMLDivElement>(null);
  const { isAuthenticated, loading: isAuthenticating } = useElectronAuth();
  const { track } = useAnalytics();

  function updateSize() {
    if (!ref.current) {
      return;
    }
    const height = ref.current.scrollHeight;

    ElectronAPI.trayResize({ height });
  }

  useEffect(() => {
    // Get the initial status of tray.
    ElectronAPI.trayVisibility().then((visible) => {
      useTrayStore.getState().setShow(visible);

      // Track when the Bridge Tray is opened
      if (visible) {
        if (loadedRoomId.current) {
          track(EVENT_NAMES.USER_OPEN_ELECTRON_TRAY, EVENT_CATEGORIES.USER, {
            studioId: loadedRoomId.current
          });
        } else {
          track(EVENT_NAMES.USER_OPEN_ELECTRON_TRAY, EVENT_CATEGORIES.USER);
        }
      }
    });

    if (!ref.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(ref.current);
    window.addEventListener('load', updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('load', updateSize);
    };
  }, []);

  useEffect(() => {
    // Hide Intercom widget in tray window
    const hideIntercomWidget = () => {
      // Hide via Intercom API if available
      if (typeof window !== 'undefined' && typeof (window as any).Intercom === 'function') {
        try {
          (window as any).Intercom('hide');
        } catch (error) {
          // Intercom may not be initialized, ignore error
        }
      }

      // Hide via DOM manipulation as fallback
      const intercomSelectors = [
        '#intercom-container',
        '#intercom-frame',
        '[id^="intercom"]',
        '[class*="intercom"]',
        '[data-intercom-target]'
      ];

      intercomSelectors.forEach((selector) => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element) => {
            if (element instanceof HTMLElement) {
              element.style.display = 'none';
              element.style.visibility = 'hidden';
              element.style.opacity = '0';
            }
          });
        } catch (error) {
          // Ignore selector errors
        }
      });
    };

    // Hide immediately
    hideIntercomWidget();

    // Set up observer to hide any Intercom widgets that appear later
    const observer = new MutationObserver(() => {
      hideIntercomWidget();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also hide on interval as additional safety measure
    const intervalId = window.setInterval(hideIntercomWidget, 500);

    return () => {
      observer.disconnect();
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <motion.div
      animate={{
        opacity: show ? 1 : 0,
        scale: show ? 1 : 0.7,
        transition: {
          ease: 'easeInOut',
          duration: 0.2
        }
      }}
      className='origin-top'
    >
      <style>{`
        .studio {
          background-color: transparent;
        }
        /* Hide Intercom widget in tray window */
        #intercom-container,
        #intercom-frame,
        [id^="intercom"],
        [class*="intercom"],
        [data-intercom-target] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}</style>
      <div className='min-h-[100px] rounded-8 bg-base-100' ref={ref}>
        {isAuthenticating || roomIdLoading ? (
          <Loader />
        ) : !isAuthenticated ? (
          <LoggedOut onSignin={ElectronAPI.openStudio} />
        ) : !loadedRoomId.current ? (
          <Unlinked onLink={ElectronAPI.openStudio} />
        ) : (
          <RoomWrapper roomId={loadedRoomId.current}>
            <ElectronTrayContent />
          </RoomWrapper>
        )}
      </div>
    </motion.div>
  );
};
